/**
 * Which launches THIS browser is following, and how far each has got (#418).
 *
 * Lives beside `launch-request.ts` rather than in `_cop/`, because both launch doors and a
 * shared `components/dashboard/` card need it: a route-private folder reaching out to a shared
 * component is the wrong direction, and this domain's other shared constants are already here.
 *
 * WHY "THIS BROWSER'S" IS THE UNIT. `launch_event` frames are org-scoped, so every operator
 * receives every launch. Toasting all of them breaks the convention every other COP stream
 * follows - `useCopAlerts` toasts only the local operator's own failures, `mission_event` goes
 * to the missions panel, coverage and rotation go to theirs. A toast in this product is the
 * consequence of YOUR press. Without this filter, operator B working an unrelated incident gets
 * a full-width red 15 s "Dock bay did not open" for a launch they did not make, in the colour
 * this palette reserves for alerts. That is a false alarm, not situational awareness.
 *
 * A second thing falls out of the same filter, and it is the reason it is a registry rather
 * than a boolean: a launch is registered only when its door answered `202 accepted`. A refusal
 * that happened BEFORE the handoff therefore never matches, so the card keeps sole ownership of
 * it - which is what stops a red "Dock bay did not open" toast appearing beside the
 * "I can see the bay, proceed" dialog that is the only launch path M-001 has.
 *
 * Module-level and deliberately not React state: the card that registers a launch unmounts
 * immediately afterwards (it closes on the 202), and the hook that reads this lives on another
 * component entirely. Cleared by a reload, which is correct - a reloaded COP has no toast to
 * carry and asks `GET /api/launches/:id` instead.
 */

/** Launch ids this browser started, in press order. */
const ownLaunches = new Set<string>();

/** Launch ids that have reached a terminal phase, so nothing may downgrade them again. */
const terminalLaunches = new Set<string>();

/**
 * Bound on how many ids are kept.
 *
 * A COP left open for a 12-hour watch would otherwise accumulate one entry per launch forever.
 * Small because the only thing these sets have to outlive is a single launch sequence.
 */
const MAX_TRACKED = 50;

function remember(set: Set<string>, launchId: string): void {
  set.add(launchId);
  if (set.size > MAX_TRACKED) {
    // Insertion-ordered, so the first key is the oldest.
    const oldest = set.values().next().value;
    if (oldest !== undefined) set.delete(oldest);
  }
}

/** The shared Sonner id, so the card that opens a toast and the hook that resolves it agree. */
export function launchToastId(launchId: string): string {
  return `launch:${launchId}`;
}

/** This browser just started this launch, and wants to be told how it ends. */
export function registerOwnLaunch(launchId: string): void {
  remember(ownLaunches, launchId);
}

export function isOwnLaunch(launchId: string): boolean {
  return ownLaunches.has(launchId);
}

/** This launch has reached `dispatched` or `refused`; later frames must not reopen it. */
export function markLaunchTerminal(launchId: string): void {
  remember(terminalLaunches, launchId);
}

export function isLaunchTerminal(launchId: string): boolean {
  return terminalLaunches.has(launchId);
}

/** Exported for tests, which must not leak tracked ids between cases. */
export function resetLaunchTracking(): void {
  ownLaunches.clear();
  terminalLaunches.clear();
}
